// Таблица платежей 134. Один компонент на экран дебиторки и на вкладку в карточке компании:
// правила показа (что считать просрочкой, чем красить стадию) должны совпадать в обоих местах,
// иначе в карточке и в списке будут разные цифры — ровно та болезнь, от которой мы уходим.
//
// Названия колонок берём из справочника полей Bitrix, а не пишем словами: переименуют поле —
// заголовок переименуется сам. Наши расчётные колонки («остаток») подписаны нами и помечены.

import { useMemo } from 'react'

import { useCompanyTitles, useFields, useRefs } from '../api/hooks'
import { fmtDate, fmtDays, fmtMoney, fmtText } from '../lib/format'
import { PAY, PAY_ENTITY, PAY_TYPE_ID } from '../lib/payments'
import type { PaymentView } from '../lib/payments'
import { bitrixItemUrl } from '../lib/smart'
import { EntityLink } from './EntityLink'

interface Props {
  rows: PaymentView[]
  /** Компактный вид для карточки компании: без компании и ответственного. */
  compact?: boolean
  /** Что показать вместо таблицы, если строк нет. */
  emptyText?: string
  /**
   * Ядро пометило выборку устаревшей (`data.stale`). Тогда пустая таблица — не «платежей
   * нет», и `emptyText` печатать нельзя: см. ветку ниже.
   */
  stale?: boolean
}

// Ссылка на карточку платежа в Bitrix (своей карточки платежа в CRM пока нет, веха В5)
// собирается общим helper'ом: адрес карточки одинаков у всех смарт-процессов, и переезд
// портала не должен править её в двух местах.

export function PaymentsTable({
  rows, compact = false, emptyText = 'Платежей нет', stale = false,
}: Props) {
  const refs = useRefs()
  const f = useFields(PAY_ENTITY)

  // названия компаний — одним запросом на всю страницу, см. useCompanyTitles
  const companyIds = useMemo(() => rows.map((r) => r.companyId), [rows])
  const titles = useCompanyTitles(compact ? [] : companyIds)

  // Заголовок из справочника, если поле там описано; иначе — наше слово, а не машинный код:
  // системные поля смарт-процесса (`title`, `stageId`) Bitrix отдаёт в справочнике не всегда.
  const h = (code: string, fallback: string) => (f.meta(code) ? f.title(code) : fallback)

  // Пустая таблица на устаревших данных — это НЕ «платежей нет». Пометку «синк отстал»
  // ставит QueryState выше, но без этой ветки прямо под ней печаталось бы «Платежей нет»:
  // два взаимоисключающих утверждения, из которых человек выбирает привычное — «долгов нет».
  // Правило то же, что в списке сделок (`data && !data.stale`). Строки, если они пришли,
  // показываем как есть: врёт не таблица, а вывод об отсутствии.
  if (rows.length === 0) {
    // Оформление — общий блок пустого состояния (styles.css §8), а не серый текст: «платежей
    // нет» это ОТВЕТ на вопрос человека, и серым мелким он читается как «таблица не
    // догрузилась». Через этот компонент пустоту показывают семь экранов (дебиторка, смарт-
    // процессы, карточки сделки, компании, контакта, судна и «Клиент 360°») — правка здесь
    // приводит к одному виду сразу все.
    return (
      <div className="empty">
        <div className="empty-title">
          {stale
            ? 'Платежи не показаны: ядро предупредило, что данные устарели. Пустой список здесь'
              + ' не означает «долгов нет» — правильной цифры у нас сейчас нет.'
            : emptyText}
        </div>
      </div>
    )
  }

  // Подписи колонок считаются ОДИН раз и идут и в шапку, и в data-label ячейки: на телефоне
  // таблица разбирается на карточки (.table-wrap.cards в styles.css), и подпись там берётся
  // из ячейки — шапки на экране уже нет. Две копии подписи разъехались бы при первом же
  // переименовании поля в Bitrix.
  const З = {
    id: 'ID',
    title: h('title', 'Название'),
    invoice: h(PAY.invoice, 'Счёт'),
    company: 'Компания',
    stage: h('stageId', 'Стадия'),
    due: h(PAY.dueDate, 'Срок оплаты'),
    remainder: 'Остаток',
    value: h(PAY.value, 'Сумма платежа'),
    deal: 'Сделка',
    resp: h(PAY.respDebt, 'Ответственный'),
  }

  return (
    <div className="table-wrap cards">
      <table>
        <thead>
          <tr>
            <th className="nowrap">{З.id}</th>
            <th>{З.title}</th>
            {!compact && <th className="nowrap">{З.invoice}</th>}
            {!compact && <th className="nowrap">{З.company}</th>}
            <th className="nowrap">{З.stage}</th>
            <th className="nowrap">{З.due}</th>
            <th className="num" title="Наш расчёт: сумма платежа минус поступление">
              {З.remainder}
            </th>
            <th className="num">{З.value}</th>
            {!compact && <th className="nowrap">{З.deal}</th>}
            {!compact && <th className="nowrap">{З.resp}</th>}
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.id}>
              <td className="mono nowrap" data-label={З.id}>
                {/* карточки платежа в CRM ещё нет — ведём в Bitrix, но честно помечаем куда */}
                <a href={bitrixItemUrl(PAY_TYPE_ID, r.id)} target="_blank" rel="noreferrer"
                   title="Открыть платёж в Bitrix (своей карточки в CRM пока нет)">
                  {r.id}
                </a>
              </td>
              <td data-label={З.title}>
                {fmtText(r.title)}
                {r.paid && <span className="badge won" style={{ marginLeft: 6 }}>оплачен</span>}
                {!r.paid && r.failed && (
                  <span className="badge lose" style={{ marginLeft: 6 }}>списан</span>
                )}
                {r.dirty && (
                  <span className="badge lose" style={{ marginLeft: 6 }}
                        title={`Признаки оплаты не сходятся: ${r.dirty}`}>
                    ?
                  </span>
                )}
                {/* Пометка, а не фильтр: строка остаётся в списке и в итогах. Причину
                    показываем в подсказке — «пробная» без объяснения это просьба поверить
                    на слово, а решать, пробная она или нет, всё равно человеку. */}
                {r.pilot && (
                  <span className="badge" style={{ marginLeft: 6 }}
                        title={`Похоже на пробную запись (${r.pilot}). В Bitrix ничего не меняем — только помечаем здесь`}>
                    пробная
                  </span>
                )}
              </td>
              {!compact && <td className="mono nowrap" data-label={З.invoice}>{fmtText(r.invoice)}</td>}
              {!compact && (
                <td className="nowrap" data-label={З.company}>
                  <EntityLink kind="company" id={r.companyId}
                              title={titles.data?.[r.companyId] || null} />
                </td>
              )}
              <td className="nowrap" data-label={З.stage}>
                <span className={`badge ${refs.itemStageKind(r.stageId)}`}>
                  {refs.itemStageName(r.stageId)}
                </span>
              </td>
              <td className="nowrap" data-label={З.due}>
                {fmtDate(r.dueDay)}
                {/* просрочку считаем по дате, а не по стадии: дата — правда, стадию двигает БП */}
                {!r.paid && r.overdueDays !== null && r.overdueDays > 0 && (
                  <div className="error" style={{ fontSize: 12 }}>+{fmtDays(r.overdueDays)}</div>
                )}
              </td>
              <td className="num" data-label={З.remainder}>
                {r.paid ? '—' : fmtMoney(r.remainder, r.currency)}
                {/* «Сумма для долгов» — вторая правда о долге: по ней считает exec-портал.
                    Показываем ТОЛЬКО когда она расходится с нашим расчётом: пока в Bitrix
                    не выяснено, кто её пишет, расхождение важнее самой цифры. */}
                {!r.paid && r.debtSum !== null && Math.abs(r.debtSum - r.remainder) >= 1 && (
                  <div className="hint"
                       title={`${f.meta(PAY.debtSum) ? f.title(PAY.debtSum) : 'Сумма для долгов'} — поле Bitrix, наш остаток считается иначе`}>
                    в Bitrix: {fmtMoney(r.debtSum, r.currency)}
                  </div>
                )}
              </td>
              <td className="num muted" data-label={З.value}
                  title={r.valueFallback
                    ? 'Money-поле платежа пусто — сумма взята из стандартного поля Bitrix'
                    : undefined}>
                {r.value ? fmtMoney(r.value.amount, r.value.currency) : '—'}
                {r.valueFallback && '*'}
              </td>
              {!compact && (
                <td className="nowrap" data-label={З.deal}><EntityLink kind="deal" id={r.dealId} /></td>
              )}
              {!compact && (
                // Подстановка работает только потому, что «не привязано» уже очищено от нуля
                // в paymentView (refId): непустая строка '0' отменяла бы её, и в колонке
                // печаталось «#0» вместо фамилии. Чью фамилию мы показали — видно в подсказке:
                // ответственный за дебиторку и ответственный карточки это разные люди.
                <td className="nowrap" data-label={З.resp}
                    title={!r.respDebtId && r.assignedById
                      ? 'В платеже ответственный за дебиторку не указан — показан ответственный карточки'
                      : undefined}>
                  {refs.userName(r.respDebtId || r.assignedById)}
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}
